import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Eyebrow, Reveal } from "@/components/site/Reveal";
import { SERVICE_PAGES } from "@/content/landings";
import Method from "@/components/home/Method";
import OneShoot from "@/components/home/OneShoot";
import PersonalCTA from "@/components/home/PersonalCTA";
import Contact from "@/components/site/Contact";

const crumbs = [{ name: "Home", to: "/" }, { name: "Services", to: "/services" }];

const ServicesPage = () => (
  <div data-testid="services-page">
    <Seo title="Services: Strategy, Content, Paid Ads, Websites & Systems" description="Strategy, on-site content production, Meta advertising, websites and custom business systems for food and product businesses in Sydney and across Australia. One team, one connected approach." schema={[breadcrumbSchema(crumbs)]} />
    <header className="pt-32 md:pt-44 pb-16 md:pb-24 max-w-7xl mx-auto px-6 md:px-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
        <Breadcrumbs items={crumbs} />
        <p className="mt-10 label-eyebrow text-[#38BDF8]">What we do</p>
        <h1 className="mt-6 font-display uppercase text-5xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.9] text-[#2E1B16] max-w-5xl">
          Five things, done <span className="font-accent font-normal normal-case text-[#38BDF8]">properly.</span>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-[#2E1B16]/60 max-w-xl leading-relaxed">
          We don't offer a menu of forty services. We do the five that move a product business, and we make sure each one feeds the next.
        </p>
      </motion.div>
    </header>

    <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
      <ol className="border-t border-[#2E1B16]/12" data-testid="services-list">
        <Reveal>
          <li className="grid md:grid-cols-[5rem_1fr_auto] gap-4 md:gap-10 items-start border-b border-[#2E1B16]/12 py-9 md:py-12">
            <span className="text-sm font-semibold text-[#38BDF8]">01</span>
            <div>
              <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16]">Strategy</h2>
              <p className="mt-3 text-lg text-[#2E1B16]/65 leading-relaxed max-w-2xl">Before anything is filmed or built we work out what the business needs to say, who needs to hear it and what a customer needs to see before they buy. Strategy is part of every engagement, not a separate product.</p>
            </div>
          </li>
        </Reveal>
        {SERVICE_PAGES.map((s, i) => (
          <Reveal key={s.path} delay={i * 0.04}>
            <li>
              <Link to={s.path} data-testid={`services-link-${s.key}`} className="group grid md:grid-cols-[5rem_1fr_auto] gap-4 md:gap-10 items-start border-b border-[#2E1B16]/12 py-9 md:py-12 hover:pl-3 transition-all duration-500">
                <span className="text-sm font-semibold text-[#38BDF8]">0{i + 2}</span>
                <div>
                  <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16] group-hover:text-[#38BDF8] transition-colors">{s.nav}</h2>
                  <p className="mt-3 text-lg text-[#2E1B16]/65 leading-relaxed max-w-2xl">{s.intro[1]}</p>
                </div>
                <ArrowUpRight size={28} className="text-[#2E1B16]/25 group-hover:text-[#38BDF8] -rotate-45 group-hover:rotate-0 transition-all duration-300 justify-self-end" />
              </Link>
            </li>
          </Reveal>
        ))}
      </ol>
    </section>

    <Method />
    <OneShoot />
    <PersonalCTA />
    <Contact pageLabel="Services page" />
  </div>
);

export default ServicesPage;
